import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { getHours, getMinutes, getSeconds, set } from 'date-fns';
import { MessageService } from 'primeng/api';
import { takeWhile } from 'rxjs/operators';
import { Channel, SMSChannel, SMSChannelTypes } from '../../../../app/enums/channel';
import { Practice } from '../../../../app/models/Practice';
import {
  getCurrentPractice,
  getCurrentPracticeSites,
} from '../../../../app/practices/state/selectors';
import { EnvironmentService } from '../../../../app/services/environment.service';
import { AppState } from '../../../../app/state/reducers';
import {
  AddCampaign,
  ClearCreatedCampaigns,
} from "../../../campaigns/state/actions";
import { TranslateService } from "@ngx-translate/core";
import { getCampaignTemplates } from '../../../templates/state/selectors';
import { Template } from '../../../models/Template';
import { GetCampaignTemplates } from '../../../templates/state/actions';
import { TemplateMergeField } from '../../../models/TemplateMergeField';
import { Media } from '../../../models/Media';
import { MediaSelectorComponent } from '../../../media/components/media-selector/media-selector.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog/dynamicdialog-ref';
import { DialogService } from 'primeng/dynamicdialog';
import { MediaType } from '../../../enums/media-type';
import { Campaign } from '../../../models/Campaign';
import { User } from '../../../models/User';
import { TemplateType } from '../../../enums/template-type';
import { Site } from '../../../models/Site';
import { getCreatedCampaignData } from '../../../campaigns/state/selectors';
import { CustomMergeFieldType } from '../../../enums/custom-merge-field-type';
import { getCurrencies } from '../../../state/selectors';
import { Currency } from '../../../models/Currency';
import { prettifyTemplateContent } from '../../../helpers/prettify-template-content';
import { practiceHasFeature } from '../../../helpers/practice-has-feature';
import { PracticeFeature } from '../../../enums/practice-feature';
import { UserService } from '../../../users/user.service';
import { CookieService } from 'ngx-cookie-service';
import { getPermittedMessagingChannels } from '../../../../app/helpers/get-permitted-messaging-channels';

interface ChannelOption {
  name: string;
  code: Channel;
  icon: string;
  inactive: boolean;
}

@Component({
  selector: 'campaign-add-form',
  templateUrl: './campaign-add-form.component.html',
  styleUrls: ['./campaign-add-form.component.scss'],
})
export class CampaignAddFormComponent implements OnInit, OnDestroy {
  @ViewChild('recipientsCsv', { static: false })
  recipientsCsv?: ElementRef<HTMLInputElement>;
  @ViewChild('templatePreview')
  templatePreview?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('firstInput') firstInput?: ElementRef<HTMLInputElement>;
  @Output() campaignCreated = new EventEmitter();
  @Input() buttonClass = '';
  alive = true;
  channels: ChannelOption[] = [];
  stepOneForm = new UntypedFormGroup({
    name: new UntypedFormControl('', Validators.required),
    message: new UntypedFormControl('', Validators.required),
    sendAt: new UntypedFormControl(new Date(), Validators.required),
    sendAtTime: new UntypedFormControl(new Date(), Validators.required),
    weekdaysOnly: new UntypedFormControl(false),
  });
  stepTwoForm = new UntypedFormGroup({
    includesColumnHeaders: new UntypedFormControl(true),

  });
  stepThreeForm = new UntypedFormGroup({
    phone: new UntypedFormControl('', Validators.required),
    clientId: new UntypedFormControl(''),
    paymentAmount: new UntypedFormControl(''),
    paymentAmountFixed: new UntypedFormControl(''),
    site: new UntypedFormControl(''),
    description: new UntypedFormControl(''),
    descriptionFixed: new UntypedFormControl(''),
    mappedFields: this.formBuilder.array([]),
  });
  selectedChannel: ChannelOption = this.channels[0];
  minDate = new Date();
  practice: Practice | null = null;
  presetMessage = '';
  deduping = false;
  recipientCount = 0;
  recipientLimit = 0;
  templates: Template[] = [];
  step = 1;
  fileUploaded = false;
  importedData: Array<string[]> = [];
  importedFilename: string | null = null;
  currentTemplate?: Template;
  showMissingCsv = false;
  stepThreeError = false;
  mappedFieldsValid = true;
  mediaType = MediaType;
  selectedMedia?: Media;
  mediaErrorKey: string | null = null;
  selectMediaDialog?: DynamicDialogRef;
  previewSrc: string | null = null;
  previewType = 'image';
  createdCampaigns: Campaign[] = [];
  dataErrors: { name: string; errors: number }[] = [];
  limitErrors: { error: string }[] = [];
  duplicates: string[] = [];
  invalidNumbers: string[] = [];
  optedOutUsers: User[] = [];
  templateType = TemplateType;
  sites: Site[] = [];
  resizeTimeout: any;
  device = 'desktop';
  previewOpen = false;
  messagePreviewText: string | null = null;
  currentTemplateFieldsString = '';
  currencies: Currency[] = [];
  practiceCurrency?: Currency;
  amountNumericError = false;
  mediaName = '';
  step3Loading = false;
  smsEnabled = true;
  whatsappEnabled = false;
  hasUsableChannel = true;
  hasMessagingEnable = false;
  hasPaymentFlag = false;
  paymentValue?: number | null = 0;
  Channel: typeof Channel = Channel;
  confirmBroadcastModal = false;
  isManualMediaSelected = false;

  constructor(
    public store: Store<AppState>,
    private messageService: MessageService,
    private environmentService: EnvironmentService,
    private formBuilder: UntypedFormBuilder,
    public dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    private cookieService: CookieService,
    private translateService: TranslateService,
  ) {
    this.subscribeToCurrentPractice();
    this.subscribeToPracticeSites();
    this.subscribeToCurrencies();

    this.recipientLimit = this.environmentService.get('campaignLimit');
  }

  ngOnInit(): void {
    this.device = this.getDevice();
    this.subscribeToWhatsappTemplates();
    this.subscribeToCreatedCampaignData();
  }

  ngAfterViewInit(): void {
    const now = new Date();
    this.stepOneForm.patchValue({
      sendAt: now,
      sendAtTime: now,
    });
  }

  ngOnDestroy(): void {
    this.store.dispatch(ClearCreatedCampaigns());
    this.alive = false;
  }

  @HostListener('change', ['$event.target.files'])
  onChanges(files: File[]): void {
    if (files && files[0]) {
      this.fileUploaded = false;
      this.importedData = [];
      this.resetFormThree();
      if (this.currentTemplate) {
        this.addMappedFields(this.currentTemplate);
      }
      this.handleCsv(files[0]);
    }
  }

  @HostListener('window:resize')
  handleResize(): void {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.device = this.getDevice();
    }, 100);
  }

  getDevice(): string {
    if (window.innerWidth <= 640) {
      return 'mobile';
    }

    return 'desktop';
  }

  onScheduleClick(): void {
    if (!this.isStepOneValid() || !this.isStepTwoValid() || !this.isStepThreeValid() || this.mediaErrorKey) {
      this.stepThreeError = true;
      return;
    }

    this.stepThreeError = false;
    this.confirmBroadcastModal = true;
  }


  sendBroadcastFinal() {
    this.submit();
    this.confirmBroadcastModal = false;
  }

  subscribeToCurrentPractice(): void {
    this.store
      .pipe(select(getCurrentPractice))
      .pipe(takeWhile(() => this.alive))
      .subscribe((practice) => {
        if (practice) {
          this.channels = getPermittedMessagingChannels(practice, true);
          this.store.dispatch(GetCampaignTemplates());
          this.practice = practice;

          this.setPracticeCurrency();
          const whatsappChannel = this.channels.find((channel) => channel.code === Channel.WHATSAPP);

          if (whatsappChannel) {
            let channelCode = Channel.WHATSAPP;
            if (
              practice.whatsapp_channel === Channel.WHATSAPP360 ||
              practice.whatsapp_channel === Channel.WHATSAPP360CLOUD
            ) {
              channelCode = practice.whatsapp_channel;
              this.setMessageTemplateSelection();
            }
          }

          this.selectedChannel = this.channels[0];
          this.hasMessagingEnable = true;

          this.recipientLimit = Math.floor(
            practice.whatsappMaxDailyConversations * 0.8
          );
        }
      });
  }

  subscribeToCurrencies(): void {
    this.store
      .pipe(select(getCurrencies))
      .pipe(takeWhile(() => this.alive))
      .subscribe((currencies) => {
        this.currencies = currencies;
        this.setPracticeCurrency();
      });
  }

  setPracticeCurrency(): void {
    if (this.currencies.length && this.practice) {
      this.practiceCurrency = this.currencies.find(
        (currency) => currency.currencyCode === this.practice?.currency
      );
    }
  }

  subscribeToPracticeSites(): void {
    this.store
      .select(getCurrentPracticeSites)
      .pipe(takeWhile(() => this.alive))
      .subscribe((sites) => {
        this.sites = sites;

        if (sites.length > 0) {
          this.stepThreeForm.get('site')?.setValue(Number(sites[0].id));
        }
      });
  }

  subscribeToWhatsappTemplates(): void {
    this.store
      .pipe(select(getCampaignTemplates))
      .pipe(takeWhile(() => this.alive))
      .subscribe((templates) => {
        this.templates = templates;
        this.setMessageTemplateSelection();
      });
  }

  setMessageTemplateSelection(): void {
    if (this.templates.length > 0) {
      this.stepOneForm.controls.message.setValue(this.templates[0].id);
      this.updateTemplatePreview();
    }
  }

  updateTemplatePreview(): void {
    this.translateService
      .get([
        'ui.campaign_add_form.phone_number',
        'ui.campaign_add_form.description',
        'ui.campaign_add_form.client_id',
        'ui.campaign_add_form.amount_to_pay',
      ])
      .pipe(takeWhile(() => this.alive))
      .subscribe((trs) => {
        const templateId = this.stepOneForm.controls.message.value ?? '';
        const template = this.templates.find(
          (tpl) => tpl.id.toString() === templateId.toString()
        );

        this.currentTemplate = template;
        this.currentTemplateFieldsString =
          trs['ui.campaign_add_form.phone_number'];

        if (template && template.type === TemplateType.CampaignWithPayment) {
          this.currentTemplateFieldsString += `, ${trs['ui.campaign_add_form.description']}, ${trs['ui.campaign_add_form.client_id']}, ${trs['ui.campaign_add_form.amount_to_pay']}`;
        }

        if (template && template.mergeFields.length > 0) {
          this.currentTemplateFieldsString +=
            ', ' +
            template.mergeFields
              .map(
                (field) =>
                  field.customName ??
                  this.translateService.instant(
                    prettifyTemplateContent(field.content)
                  )
              )
              .join(', ');
        }

        if (template?.attachMedia && template.defaultMedia) {
          if (!this.isManualMediaSelected) {
            const media = template.defaultMedia as Media;
            this.selectedMedia = media;
            this.previewSrc = media.signedUrl;
            this.previewType = media.mime?.startsWith('image')
              ? 'image'
              : media.mime?.startsWith('video')
              ? 'video'
              : 'pdf';
            this.mediaName = this.formatMediaName(media);
          }
        } else if (!template?.attachMedia || !template?.defaultMedia) {
          if (!this.isManualMediaSelected) {
            this.selectedMedia = undefined;
            this.previewSrc = null;
            this.previewType = '';
            this.mediaName = '';
            this.mediaErrorKey = null;
          }
        }

        this.messagePreviewText = template ? template.body : null;

        if (this.templatePreview && template) {
          this.templatePreview.nativeElement.value = template.body;
        }

        if (template) {
          this.addMappedFields(template);
        }
      });
  }

  openFileBrowser(): void {
    if (this.recipientsCsv) {
      this.recipientsCsv.nativeElement.value = '';
    }

    this.recipientsCsv?.nativeElement.click();
  }

  handleCsv(file: File): void {
    const fileReader = new FileReader();
    this.showMissingCsv = false;
    this.fileUploaded = true;
    this.importedFilename = file.name;
    fileReader.onload = async (e: any) => {
      const fileContent = e.target.result;
      const data: Array<string[]> = [];
      const regex = /"([^"]*?)"/g;
      const result = fileContent.replace(
        regex,
        (match: string, capturedString: string) => {
          const stringWithoutNewlines = capturedString.replace(/\n/g, '');
          return `"${stringWithoutNewlines}"`;
        }
      );

      result.split(/\r?\n/).forEach((row: string) => {
        const columns = row.split(/,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)/);
        if (columns.length > 0) {
          const processedColumns = columns.map((column) =>
            column.replace(/"/g, '')
          );
          data.push(processedColumns);
        }
      });

      this.importedData = data;
    };

    if (!file) {
      return;
    }

    if (file.type !== "text/csv") {
      this.translateService.get(['ui.campaign_add_form.error', 'ui.campaign_add_form.not_csv']).pipe(takeWhile(_ => this.alive)).subscribe(trs => {
        this.messageService.add({
          severity: "error",
          summary: trs['ui.campaign_add_form.error'],
          detail: trs['ui.campaign_add_form.not_csv'],
          life: 30000,
        });
      })
      return;
    }

    fileReader.readAsText(file);
  }

  goToStep(step: number): void {
    let valid = false;

    if (this.step === 1 && step === 2) {
      this.hasPaymentFlag = false;
      valid = this.isStepOneValid();
      if (this.selectedChannel.name === Channel.WHATSAPP) {

        this.stepTwoForm.addControl('excluded_client', new UntypedFormControl(false, Validators.requiredTrue))
        this.stepTwoForm.addControl('active_patients', new UntypedFormControl(false, Validators.requiredTrue))
        this.stepTwoForm.addControl('contact_details', new UntypedFormControl(false, Validators.requiredTrue))
        this.stepTwoForm.addControl('marketing_relates', new UntypedFormControl(false, Validators.requiredTrue))
      } else if (this.selectedChannel.name === Channel.SMS) {
        this.stepTwoForm.addControl('permission', new UntypedFormControl(false, Validators.requiredTrue))
      }
    }

    if (this.step === 2 && step === 3) {
      this.showMissingCsv = true;
      valid = this.isStepTwoValid();
    }

    if (valid) {
      this.step = step;
    }

    if (this.step === 3) {
      this.hasPaymentFlag = !!this.currentTemplate?.paymentButton;
    }

  }

  goToPreviousStep(): void {
    this.step = this.step - 1;
    this.hasPaymentFlag = false;
  }

  resetForms(): void {

    this.resetFormThree();
    this.resetFormTwo();
    this.resetFormOne();
    this.createdCampaigns = [];
    this.dataErrors = [];
    this.limitErrors = [];
    this.duplicates = [];
    this.invalidNumbers = [];
    this.optedOutUsers = [];
  }

  resetFormOne(): void {
    this.selectedChannel = this.channels[0];
    this.stepOneForm.controls.name.setValue('');
    this.stepOneForm.controls.message.setValue(this.presetMessage);
    this.stepOneForm.controls.sendAt.setValue(null);
    this.stepOneForm.controls.sendAtTime.setValue(null);
    this.stepOneForm.controls.weekdaysOnly.setValue(false);
    this.recipientCount = 0;
    this.selectedMedia = undefined;
    this.previewSrc = null;
    this.mediaErrorKey = null;

    Object.keys(this.stepOneForm.controls).forEach((field) => {
      const control = this.stepOneForm.get(field);
      control?.markAsPristine({ onlySelf: true });
      control?.markAsUntouched({ onlySelf: true });
    });
  }

  resetFormTwo(): void {
    this.stepTwoForm.controls.includesColumnHeaders.setValue(true);
    if (this.selectedChannel.name === Channel.WHATSAPP) {
      this.stepTwoForm.controls.excluded_client.setValue(false);
      this.stepTwoForm.controls.active_patients.setValue(false);
      this.stepTwoForm.controls.contact_details.setValue(false);
      this.stepTwoForm.controls.marketing_relates.setValue(false);
    } else if (this.selectedChannel.name === Channel.SMS) {
      this.stepTwoForm.controls.permission.setValue(false);
    }
    Object.keys(this.stepTwoForm.controls).forEach((field) => {
      const control = this.stepTwoForm.get(field);
      control?.markAsPristine({ onlySelf: true });
      control?.markAsUntouched({ onlySelf: true });
    });
  }

  resetFormThree(): void {
    this.stepThreeForm.controls.phone.setValue('');
    this.stepThreeForm.controls.clientId.setValue('');
    this.stepThreeForm.controls.paymentAmount.setValue('');
    this.stepThreeForm.controls.paymentAmountFixed.setValue('');
    this.stepThreeForm.controls.site.setValue('');
    this.stepThreeForm.controls.description.setValue('');
    this.stepThreeForm.controls.descriptionFixed.setValue('');

    this.removeMappedFields();
    this.stepThreeError = false;
    this.amountNumericError = false;

    Object.keys(this.stepThreeForm.controls).forEach((field) => {
      const control = this.stepThreeForm.get(field);
      control?.markAsPristine({ onlySelf: true });
      control?.markAsUntouched({ onlySelf: true });
    });

    this.updateTemplatePreview();
  }

  mappedFields(): UntypedFormArray | null {
    return this.stepThreeForm.get('mappedFields') as UntypedFormArray;
  }

  addMappedFields(template: Template): void {
    this.removeMappedFields();
    template.mergeFields.forEach((mergeField: TemplateMergeField) => {
      this.addMappedField(mergeField);
    });
  }

  addMappedField(field: TemplateMergeField): void {
    const mappedFields = this.mappedFields();
    if (mappedFields) {
      mappedFields.push(this.newMappedField(field));
    }
  }

  newMappedField(field: TemplateMergeField): UntypedFormGroup {
    return this.formBuilder.group({
      fieldId: new UntypedFormControl(field.id),
      customName: new UntypedFormControl(field.customName),
      content: new UntypedFormControl(field.content),
      mappedColumn: new UntypedFormControl(''),
      fixedValue: new UntypedFormControl(''),
    });
  }

  removeMappedFields(): void {
    const mappedFields = this.mappedFields();
    if (mappedFields) {
      while (mappedFields.length !== 0) {
        mappedFields.removeAt(0);
      }
    }
  }

  submitDraft(): void {
    this.submit(true);
  }

  isStepOneValid(): boolean {
    this.mediaErrorKey = null;

    Object.keys(this.stepOneForm.controls).forEach((field) => {
      const control = this.stepOneForm.get(field);
      control?.markAsTouched({ onlySelf: true });
    });

    if (
      this.currentTemplate &&
      this.currentTemplate.attachMedia &&
      this.currentTemplate.mediaType
    ) {
      if (!this.selectedMedia) {
        this.mediaErrorKey = "ui.campaign_add_form.select_template_media";
      } else {
        switch (this.currentTemplate.mediaType) {
          case MediaType.Image:
            if (!this.selectedMedia.mime.includes("image/")) {
              this.mediaErrorKey =
                "ui.campaign_add_form.select_template_image";
            }
            break;

          case MediaType.Video:
            if (!this.selectedMedia.mime.includes("video/")) {
              this.mediaErrorKey =
                "ui.campaign_add_form.select_template_video";
            }
            break;

          case MediaType.PDF:
            if (this.selectedMedia.mime !== "application/pdf") {
              this.mediaErrorKey =
                "ui.campaign_add_form.select_template_pdf";
            }
            break;
        }
      }
    }

    if (!this.stepOneForm.valid || this.mediaErrorKey) {
      return false;
    }
    return true;
  }

  isStepTwoValid(): boolean {
    Object.keys(this.stepTwoForm.controls).forEach((field) => {
      const control = this.stepTwoForm.get(field);
      control?.markAsTouched({ onlySelf: true });
    });

    if (this.importedData.length === 0 || !this.stepTwoForm.valid) {
      return false;
    }

    return true;
  }

  isStepThreeValid(): boolean {
    Object.keys(this.stepThreeForm.controls).forEach((field) => {
      const control = this.stepThreeForm.get(field);
      control?.markAsTouched({ onlySelf: true });
    });

    this.mappedFieldsValid = true;

    const fields = this.stepThreeForm.get('mappedFields')?.value;
    let allValid = true;

    if (fields) {
      fields.forEach((field: any) => {
        if (field.mappedColumn === 'fixed' && !field.fixedValue) {
          allValid = false;
        } else if (!field.mappedColumn) {
          allValid = false;
        }
      });
    }

    if (!allValid) {
      this.mappedFieldsValid = false;
      return false;
    }

    if (
      this.currentTemplate &&
      this.currentTemplate.type === TemplateType.CampaignWithPayment
    ) {
      if (!this.stepThreeForm.get('clientId')?.value) {
        this.mappedFieldsValid = false;
        return false;
      }

      if (!this.stepThreeForm.get('paymentAmount')?.value) {
        this.mappedFieldsValid = false;
        return false;
      }

      if (
        this.stepThreeForm.get('paymentAmount')?.value === 'fixed' &&
        !this.stepThreeForm.get('paymentAmountFixed')?.value
      ) {
        this.mappedFieldsValid = false;
        return false;
      }
    }

    if (!this.stepThreeForm.valid) {
      return false;
    }

    return true;
  }

  submit(draft: boolean = false): void {
    this.step3Loading = true;

    if (!this.isStepOneValid() || !this.isStepTwoValid() || this.mediaErrorKey) {
      this.step3Loading = false;
      return;
    }

    if (!this.isStepThreeValid()) {
      this.stepThreeError = true;
      this.step3Loading = false;
      return;
    }

    this.stepThreeError = false;

    if (this.practice) {
      const sendAt = set(this.stepOneForm.controls.sendAt.value, {
        hours: getHours(this.stepOneForm.controls.sendAtTime.value),
        minutes: getMinutes(this.stepOneForm.controls.sendAtTime.value),
        seconds: getSeconds(this.stepOneForm.controls.sendAtTime.value),
      }).toISOString();

      let channel: string = this.selectedChannel.code;
      if (channel === Channel.SMS) {
        channel = this.practice.sms_channel === SMSChannel.Twilio ? SMSChannelTypes.SMS : SMSChannelTypes.SMSInfobip;
      }

      this.store.dispatch(
        AddCampaign({
          campaign: {
            name: this.stepOneForm.controls.name
              ? this.stepOneForm.controls.name.value.toString()
              : '',
            message: this.stepOneForm.controls.message
              ? this.stepOneForm.controls.message.value.toString()
              : '',
            mediaId: this.selectedMedia?.id,
            sendAt,
            channel,
            practiceId: this.practice.id,
            weekdaysOnly: this.stepOneForm.controls.weekdaysOnly.value,
            selectedTemplate: true,
            draft,
            importedData: this.importedData,
            includesColumnHeaders:
              this.stepTwoForm.controls.includesColumnHeaders.value,
            phoneMapping: {
              mappedColumn: this.stepThreeForm.controls.phone.value,
            },
            clientIdMapping: {
              mappedColumn: this.stepThreeForm.controls.clientId.value,
            },
            paymentAmountMapping: {
              mappedColumn: this.stepThreeForm.controls.paymentAmount.value,
              fixedValue: this.stepThreeForm.controls.paymentAmountFixed.value,
            },
            descriptionMapping: {
              mappedColumn: this.stepThreeForm.controls.description.value,
              fixedValue: this.stepThreeForm.controls.descriptionFixed.value,
            },
            fieldMapping: [...this.stepThreeForm.get('mappedFields')?.value],
            siteId: this.stepThreeForm.controls.site.value,
          },
        })
      );
    }
  }

  subscribeToCreatedCampaignData(): void {
    this.store
      .select(getCreatedCampaignData)
      .pipe(takeWhile(() => this.alive))
      .subscribe((data) => {
        if (data.createdCampaigns) {
          if (this.createdCampaigns !== data.createdCampaigns) {
            this.step3Loading = false;
            this.createdCampaigns = data.createdCampaigns;
            this.dataErrors = data.dataErrors;
            this.limitErrors = data.limitErrors;
            this.duplicates = data.duplicates;
            this.invalidNumbers = data.invalidNumbers;
            this.optedOutUsers = data.optedOutUsers;
            this.step = 4;
          }
        }
      });
  }

  selectMedia(): void {
    this.translateService.get('ui.campaign_add_form.select_media').pipe(takeWhile(_ => this.alive)).subscribe(header => {

      this.selectMediaDialog = this.dialogService.open(MediaSelectorComponent, {
        header,
        modal: true,
        width: "1000px",
        baseZIndex: 10000,
        data: {
          type: this.currentTemplate?.mediaType,
        },
      });

      this.selectMediaDialog.onClose.subscribe((media: Media) => {
        if (media) {
          this.selectedMedia = media;
          this.isManualMediaSelected = true;
          this.mediaErrorKey = null; 

          const previewTypeMap: { [key: string]: string } = {
            "image/": "image",
            "video/": "video",
            "/pdf": "pdf",
          };

          const previewType = Object.keys(previewTypeMap).find((type) =>
            media.mime.includes(type),
          );

          if (previewType) {
            this.previewType = previewTypeMap[previewType];
            this.previewSrc = media.signedUrl;
          }
          this.mediaName = this.formatMediaName(media);
          this.cdr.detectChanges();
        }
      });
    })
  }

  formatMediaName(media: { mime: string; name: string }): string {
    const extension = media.mime.split('/')[1]; 
    return `${media.name}.${extension}`;
  }

  removeMedia(): void {
    const shouldRestoreDefault = this.isManualMediaSelected && this.currentTemplate?.attachMedia && this.currentTemplate?.defaultMedia;
    
    this.selectedMedia = undefined;
    this.previewSrc = null;
    this.isManualMediaSelected = false;
    this.mediaName = '';
    this.mediaErrorKey = null;
    this.previewType = '';
    
    if (shouldRestoreDefault) {
      const media = this.currentTemplate!.defaultMedia as Media;
      this.selectedMedia = media;
      this.previewSrc = media.signedUrl;
      this.previewType = media.mime?.startsWith('image')
        ? 'image'
        : media.mime?.startsWith('video')
        ? 'video'
        : 'pdf';
      this.mediaName = this.formatMediaName(media);
    }
    this.cdr.detectChanges();
  }

  close(): void {
    this.store.dispatch(ClearCreatedCampaigns());
    this.resetForms();
    this.campaignCreated.emit();
  }

  togglePreview(): void {
    this.previewOpen = !this.previewOpen;
  }

  updateMapping(): void {
    if (this.currentTemplate) {
      const rowNumber = this.stepTwoForm.controls.includesColumnHeaders.value
        ? 1
        : 0;

      let tmpBody = this.currentTemplate?.body;

      const mergeFields = this.currentTemplate?.mergeFields;

      const fields = this.stepThreeForm.get('mappedFields')?.value;

      fields.forEach((item: any) => {
        if (this.currentTemplate) {
          mergeFields.forEach((mergeField: TemplateMergeField) => {
            if (item.fieldId === mergeField.id && item.mappedColumn) {
              let prefix = '';

              if (mergeField.type === CustomMergeFieldType.Currency) {
                prefix = this.practiceCurrency?.currencySymbol ?? '';
              }

              let replacement = '';
              if (item.mappedColumn === 'fixed') {
                replacement = item.fixedValue;
              } else {
                replacement =
                  this.importedData[rowNumber][Number(item.mappedColumn)];
              }

              if (!replacement.includes(prefix)) {
                replacement = prefix + replacement;
              }

              tmpBody = tmpBody?.replace(mergeField.placeholder, replacement);
            }
          });
        }
      });

      this.messagePreviewText = tmpBody;
    }
  }

  showPreview(): void {
    this.previewOpen = true;
  }

  hidePreview(): void {
    this.previewOpen = false;
  }

  validatePaymentAmountSelection(): void {
    this.amountNumericError = false;

    const selectedAmountColumn =
      this.stepThreeForm.controls.paymentAmount.value;

    if (selectedAmountColumn && selectedAmountColumn !== 'fixed') {
      this.importedData.forEach((datum, index) => {
        if (
          this.stepTwoForm.controls.includesColumnHeaders.value &&
          index === 0
        ) {
          return;
        }

        if (isNaN(Number(datum[Number(selectedAmountColumn)]))) {
          const selectpayment = this.importedData[1];
          this.paymentValue = Number(selectpayment[Number(selectedAmountColumn)]);
          if (isNaN(this.paymentValue)) {
            this.paymentValue = 0;
            this.amountNumericError = true;
          }
        }
      });
    } else if (selectedAmountColumn === "fixed") {
      this.paymentValue = this.stepThreeForm.controls.paymentAmountFixed.value;
      if (isNaN(Number(this.stepThreeForm.controls.paymentAmountFixed.value))) {
        this.amountNumericError = true;
      }
    }
  }

  getPaymentString(): string {
    const selectedAmountColumn = this.stepThreeForm.controls.paymentAmount.value;

    if (selectedAmountColumn && selectedAmountColumn !== 'fixed') {
      let columnValue = this.importedData[0][selectedAmountColumn];

      if (
        this.stepTwoForm.controls.includesColumnHeaders.value
      ) {
        columnValue = this.importedData[1][selectedAmountColumn];
      }

      return (Number(columnValue)).toFixed(2);

    } else if (selectedAmountColumn && selectedAmountColumn === 'fixed') {
      return this.stepThreeForm.controls.paymentAmountFixed.value ?? "0.00";
    }

    return "0.00";
  }

  getPaymentDescriptionString(): string {
    const descColumn = this.stepThreeForm.controls.description.value;

    if (descColumn && descColumn !== 'fixed') {
      let columnValue = this.importedData[0][descColumn];

      if (
        this.stepTwoForm.controls.includesColumnHeaders.value
      ) {
        columnValue = this.importedData[1][descColumn];
      }

      return columnValue;
    } else if (descColumn && descColumn === 'fixed') {
      return this.stepThreeForm.controls.descriptionFixed.value ?? "";
    }

    return "";
  }
}

